<template>
	<header-chat-resend-modal
		ref="$resendMessages"
		:title="$t('chat_forward_select')"
		@on-resend="selectedMessages = []"
	/>
	<bill-info-modal ref="$billInfoModal" />
	<div
		ref="$chatPanel"
		class="chat-messages-panel"
		:style="keyboardPanelStyle"
	>
		<app-loader
			v-if="isLoadingChat || isFirstLoadingFromSearch"
			:style="loaderStyle"
		/>
		<div
			class="chat-messages-panel__messages"
			ref="$messagesArea"
		>
			<div
				v-if="selectedMessages.length"
				class="chat-messages-panel__messages-reply"
			>
				<div class="chat-messages-panel__messages-reply-count">
					{{ $t('chat_selected_messages', { count: selectedMessages.length }) }}
					<app-icon
						name="close-bold"
						width="12px"
						color="#D2D2D2"
						hover-color="#4D4D4D"
						cursor="pointer"
						:style="{ marginTop: '-2px' }"
						@click.stop="onDropSelected"
					/>
				</div>
				<div class="chat-messages-panel__messages-reply-spacer" />
				<div class="chat-messages-panel__messages-reply-btn">
					<app-icon
						class="chat-messages-panel__messages-reply-btn-icon"
						name="reply-right"
						width="18px"
					/>
					<span
						class="chat-messages-panel__messages-reply-btn-text"
						@click="resendMessages"
					>
						{{ $t('chat_forward') }}
					</span>
				</div>
			</div>
			<chat-message-component
				v-for="message in chatMessages"
				:key="message.Id"
				:message="message"
				:selected="selectedMessages.includes(message.Id)"
				:mode="props.mode"
				@click="toggleMessage(message, $event)"
				@mouseenter="readMessage(message)"
				@set-answered="setMessageAnswered"
				@open-bill="openBillInfo"
				@navigated="emit('on-navigate')"
			/>
		</div>
		<div
			v-if="isLoadingPrev"
			class="chat-messages-panel__pagination-loader chat-messages-panel__pagination-loader--top"
		>
			<app-loader/>
		</div>
		<div
			v-if="isLoadingNext"
			class="chat-messages-panel__pagination-loader chat-messages-panel__pagination-loader--bottom"
		>
			<app-loader/>
		</div>
		<div class="chat-messages-panel__input">
			<div
				v-if="isScrollBtnVisible"
				class="chat-messages-panel__scroll-btn"
				@click="scrollDown"
			>
				<app-icon
					name="arrow-down"
					width="24px"
					color="#fff"
				/>
			</div>
			<template v-if="attachOrResendMessages && props.mode === 'chat'">
				<div
					v-if="attachOrResendMessages.length === 1"
					class="chat-messages-panel__input-answered-message"
				>
					<div class="answered-message__lable" />
					<div class="answered-message__body">
						<div class="answered-message__body-header">
							<div class="answered-message__body-header-name">
								{{ attachOrResendMessages[0].WorkerName }}
							</div>
							<div class="answered-message__body-header-date">
								{{ getDialogDate(attachOrResendMessages[0].Date) }}
							</div>
							<app-icon
								class="answered-message__body-header-close"
								name="close-bold"
								width="12px"
								color="#D2D2D2"
								hover-color="#4D4D4D"
								cursor="pointer"
								@click.stop="chatStore.setResendMessages(null)"
							/>
						</div>
						<div
							class="answered-message__body-text"
							v-html="answeredMessageHtml"
							@click="onMessageClick"
						/>
					</div>
				</div>
				<div
					v-else
					class="chat-messages-panel__input-answered-message"
				>
					<div class="answered-message__lable" />
					<div class="answered-message__body">
						<div class="answered-message__body-header">
							<app-icon
								class="answered-message__body-header-close"
								name="close-bold"
								width="12px"
								color="#D2D2D2"
								hover-color="#4D4D4D"
								cursor="pointer"
								@click.stop="chatStore.setResendMessages(null)"
							/>
						</div>
						<div class="answered-message__body-text">
							{{ $tc('chat_messages_count', attachOrResendMessages?.length ?? 0) }}
						</div>
					</div>
				</div>
			</template>
			<div
				v-show="props.mode === 'chat'"
				class="chat-messages-panel__input-field"
			>
				<app-custom-textarea
					ref="inputRef"
					v-model="message"
					:placeholder="$t('chat_message_placeholder')"
					:min-height="32"
					:max-height="100"
					:new-line-mode="newLineMode"
					@focus="onChatInputFocus"
					@blur="onChatInputBlur"
					@enter="sendMessage"
					@ctrl-enter="sendMessage"
				/>
				<app-tooltip
					class="chat-search-panel__edit-btn"
					:text="$t(sendHintKey)"
				>
					<template #activator>
						<app-icon
							class="chat-messages-panel__input-field-btn"
							name="send-message"
							width="16px"
							:color="message.trim().length ? '#3F884F' : '#D2D2D2'"
							hover-color="#3F884F"
							cursor="pointer"
							@click="sendMessage"
						/>
					</template>
				</app-tooltip>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch
} from "vue";
import {
	ChatMessage,
	ChatModes
} from "@/store/chats";
import { headerStore } from "@/store/header";
import { workerStore } from "@/store/worker";
import moment from "moment/moment";
import AppLoader from "@/components/common/AppLoader.vue";
import ChatMessageComponent from "@/components/main/chat-panel/ChatMessage.vue";
import AppCustomTextarea from "@/components/ui-kit/textarea/AppCustomTextarea.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import HeaderChatResendModal from "@/components/layout/AppHeader/chat-panel/modals/HeaderChatResendModal.vue";
import BillInfoModal from "@/components/main/client-block-page/modals/BillInfoModal.vue";
import { applyChatMessagePlaceholders } from "@/utils/textHelpers";
import { useChatInputFocus } from "@/composables/useChatInputFocus";
import { useChatKeyboardViewport } from "@/composables/useChatKeyboardViewport";
import { useChatMessageLinks } from "@/composables/useChatMessageLinks";
import { useMessageSendingWay } from "@/composables/useMessageSendingWay";
import { debounce } from "lodash";

const chatStore = headerStore()
const wStore = workerStore()
const props = defineProps<{ mode: ChatModes }>()
const emit = defineEmits<{
	(e: 'on-open-message', value: ChatMessage): void,
	(e: 'on-navigate'): void,
}>()

const $resendMessages = ref<typeof HeaderChatResendModal | null>(null)
const $billInfoModal = ref<InstanceType<typeof BillInfoModal> | null>(null)
const { inputRef, focusSync, focusAsync } = useChatInputFocus()

const openBillInfo = (billId: number) => $billInfoModal.value?.onOpen(billId)
const { onMessageClick } = useChatMessageLinks({
	onOpenBill: openBillInfo,
	onNavigated: () => emit('on-navigate'),
})
const $messagesArea = ref<HTMLDivElement | null>(null)
const $chatPanel = ref<HTMLElement | null>(null)
const {
	isInputFocused,
	panelStyle: keyboardPanelStyle,
	onInputFocus: onChatInputFocus,
	onInputBlur: onChatInputBlur,
	scrollToLatest,
} = useChatKeyboardViewport($chatPanel, $messagesArea)
const message = ref<string>("")
const selectedMessages = ref<number[]>([])
const isScrollBtnVisible = ref(false)
const isFirstLoading = ref(true)
const isFirstLoadingFromSearch = ref(true)
const currentChatScrollTopPosition = ref<number | null>(null)

const worker = computed(() => wStore.workerProfile)
const isActiveMode = computed(() => props.mode === 'chat')
const chat = computed(() => chatStore.getOpenedChat)
const chatLoadingState = computed(() => chatStore.getOpenedChatLoadState)
const isLoadingChat = computed(() => chatStore.isChatMessagesLoading)
const isLoadingPrev = computed(() => chatStore.loadingPrev)
const isLoadingNext = computed(() => chatStore.loadingNext)
const chatMessages = computed(() =>
	props.mode === 'chat'
		? chatStore.getOpenedChatMessages
		: chatStore.getSearchedMessages
)
const chatMessagesHistory = computed(() => chatStore.getOpenedChatsHistory)
const resendMessagesList = computed(() => chatStore.resendMessages)
const { newLineMode, sendHintKey } = useMessageSendingWay()

const attachOrResendMessages = computed(() =>
	resendMessagesList.value && resendMessagesList.value.chatId === chat.value?.Id
		? resendMessagesList.value.messages
		: null
)

const answeredMessageHtml = computed(() => {
	const answered = attachOrResendMessages.value?.[0]
	if (!answered) {
		return ''
	}

	return applyChatMessagePlaceholders(answered.Text ?? '', answered.Links, answered.Bills)
		.replaceAll('\n', '<br>')
})

const toggleMessage = (message: ChatMessage, event?: MouseEvent) => {
	if ((event?.target as HTMLElement)?.closest('.link-message')) {
		return
	}
	if (props.mode === 'search-messages') {
		emit('on-open-message', message)
		return
	}

	if (selectedMessages.value.includes(message.Id)) {
		selectedMessages.value.splice(selectedMessages.value.indexOf(message.Id), 1)
	} else {
		selectedMessages.value.push(message.Id)
	}
}

const readMessage = (message: ChatMessage) => {
	if (message.Unreaded && worker.value?.Id !== message.WorkerId) {
		chatStore.readChatMessage(message.Id)
	}
}

const setMessageAnswered = (messageId: number) => {
	chatStore.setResendMessages({
		chatId: chat.value?.Id as number,
		messages: [chatMessages.value.find(message => message.Id === messageId)!]
	})
	focusAsync()
}

const onDropSelected = () => selectedMessages.value = []

const getDialogDate = (date: string) => moment(date).format("DD.MM.YYYY HH:mm");

const scrollToBottom = () => {
	const el = $messagesArea.value
	if (el) {
		el.scrollTop = el.scrollHeight
	}
}

const scrollDown = () => nextTick(scrollToBottom)

const scrollToPosition = (position: number) => nextTick(
	() => {
		$messagesArea.value ? $messagesArea.value.scrollTop = position : null
	}
)

const sendMessage = async () => {
	if (message.value && chat.value?.Id) {
		try {
			const messageText = message.value
			const attaches = JSON.parse(JSON.stringify(attachOrResendMessages.value))
			message.value = ''
			chatStore.setResendMessages(null)
			const lastMessageId = chatMessages.value[chatMessages.value.length - 1]?.Id
			await chatStore.createMessage({
				chatId: chat.value?.Id,
				text: messageText.replace(/\n{3,}/g, '\n\n'),
				attachedMessageIds: attaches
					? attaches.map(message => message.Id)
					: undefined,
			}, getMessageDate(), lastMessageId)
		} catch (e) {
			console.log(e)
		}
	}
}

const resendMessages = async () => {
	$resendMessages.value?.onOpen(chatMessages.value.filter(message => selectedMessages.value.includes(message.Id)))
}

const getMessageDate = () => {
	const date = new Date();

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	const seconds = String(date.getSeconds()).padStart(2, '0');
	const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
}

const isAdjustingScroll = ref(false)
const paginationCooldownUntil = ref(0)

const preservePrependAnchor = async (action: () => Promise<void>) => {
	const el = $messagesArea.value
	if (!el) {
		await action();
		return
	}

	const prevHeight = el.scrollHeight
	const prevTop = el.scrollTop
	await action()
	await nextTick()
	await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
	if ($messagesArea.value) {
		isAdjustingScroll.value = true
		const newTop = prevTop + ($messagesArea.value.scrollHeight - prevHeight)
		$messagesArea.value.scrollTop = newTop
		currentChatScrollTopPosition.value = newTop
		requestAnimationFrame(() => { isAdjustingScroll.value = false })
	}
}

const loadPrev = async () => {
	if (!chat.value || !chatMessages.value.length) return

	if (chatStore.loadingPrev || chatStore.loadingNext) return

	if (!chatLoadingState.value?.canLoadPrev) return

	const chatId = chat.value.Id
	const date = chatMessages.value[0].Date
	await preservePrependAnchor(() => chatStore.getPrevMessages(chatId, date))
	paginationCooldownUntil.value = Date.now() + 250
}

const loadNext = async () => {
	if (!chat.value || !chatMessages.value.length) return

	if (chatStore.loadingPrev || chatStore.loadingNext) return

	if (!chatLoadingState.value?.canLoadNext) return

	await chatStore.getNextMessages(chat.value.Id, chatMessages.value[chatMessages.value.length - 1].Date)
	paginationCooldownUntil.value = Date.now() + 250
}

const loadPrevThrottled = debounce(loadPrev, 200, { leading: false, trailing: true })
const loadNextThrottled = debounce(loadNext, 200, { leading: false, trailing: true })

const updateChatMessages = () => {
	const el = $messagesArea.value
	if (!el || isAdjustingScroll.value || Date.now() < paginationCooldownUntil.value) return

	const scrollTop = el.scrollTop
	const prev = currentChatScrollTopPosition.value

	if (prev !== null && scrollTop < 500 && scrollTop < prev) {
		loadNextThrottled.cancel()
		loadPrevThrottled()
	} else if (prev !== null && scrollTop + el.offsetHeight + 500 > el.scrollHeight && scrollTop > prev) {
		loadPrevThrottled.cancel()
		loadNextThrottled()
	}

	currentChatScrollTopPosition.value = scrollTop
}

const setChatPosition = (scrollTop?: number) => {
	if (props.mode === 'chat') {
		if (scrollTop) {
			scrollToPosition(scrollTop)
		} else {
			const history = chatMessagesHistory.value.find(c => c.Id === chat.value?.Id)
			if (typeof history?.ScrollTop === 'number') {
				scrollToPosition(history.ScrollTop)
			} else {
				scrollDown()
			}
		}
	}
}

watch(chatMessages, () => {
	if (isFirstLoading.value) {
		if (!chatStore.pendingCenterMessageId) {
			setChatPosition()
		}

		isFirstLoading.value = false
	}
});

// Переменные чисто для отработки вотчеров
let prevChatId: number | null = null
let prevMessagesLen = 0
let prevLastMessageId: number | null = null

watch(chatMessages, (newMessages) => {
	const last = newMessages[newMessages.length - 1] ?? null
	const chatId = chatStore.getOpenedChatId

	const isAppend =
		isActiveMode.value &&
		chatId === prevChatId &&
		prevMessagesLen > 0 &&
		newMessages.length > prevMessagesLen &&
		last?.Id !== prevLastMessageId

	prevChatId = chatId
	prevMessagesLen = newMessages.length
	prevLastMessageId = last?.Id ?? null

	if (!isAppend) {
		return
	}

	if (worker.value?.Id === last?.WorkerId || !isScrollBtnVisible.value) {
		scrollToBottom()
	}
}, { flush: 'post' });

watch(() => chatStore.getOpenedChatId, (newId, oldId) => {
	if (!newId || newId === oldId || !isActiveMode.value) return
	if (!chatStore.pendingCenterMessageId) {
		setChatPosition()
	}

	focusAsync()
}, { flush: 'post' });

let scrollHandler: (() => void) | null = null

const centerOnTargetMessage = async () => {
	if (!chatStore.pendingCenterMessageId) return

	const targetId = chatStore.pendingCenterMessageId
	await nextTick()
	const tryScroll = (attempt = 0) => {
		const element = document.getElementById(`message-${targetId}`) as HTMLElement | null
		if (element) {
			element.scrollIntoView({ block: 'center', behavior: 'auto' })
			if ($messagesArea.value) currentChatScrollTopPosition.value = $messagesArea.value.scrollTop

			element.style.backgroundColor = '#D2D2D2'
			setTimeout(() => { element.style.backgroundColor = 'transparent' }, 1000)
			isFirstLoadingFromSearch.value = false
			chatStore.pendingCenterMessageId = null
			return
		}
		if (attempt < 15) {
			requestAnimationFrame(() => tryScroll(attempt + 1))
		} else {
			isFirstLoadingFromSearch.value = false
			chatStore.pendingCenterMessageId = null
		}
	}
	tryScroll()
}

watch(isLoadingChat, async (newVal) => {
	if (!newVal) {
		await nextTick()
		if (isInputFocused.value) await scrollToLatest()
		currentChatScrollTopPosition.value = $messagesArea.value?.scrollTop as number
		if (chatStore.pendingCenterMessageId) {
			await centerOnTargetMessage()
		}
	}
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 1)'
}))

onMounted(async () => {
	isFirstLoadingFromSearch.value = !!chatStore.pendingCenterMessageId

	if (isActiveMode.value) {
		focusAsync()
	}

	await nextTick()

	if (chat.value && chatMessages.value.length > 0) {
		if (chatStore.pendingCenterMessageId) {
			await centerOnTargetMessage()
		} else {
			setChatPosition()
		}
		isFirstLoading.value = false
		currentChatScrollTopPosition.value = $messagesArea.value?.scrollTop ?? null
	}

	prevChatId = chatStore.getOpenedChatId
	prevMessagesLen = chatMessages.value.length
	prevLastMessageId = chatMessages.value[chatMessages.value.length - 1]?.Id ?? null

	scrollHandler = () => {
		if (!$messagesArea.value) return
		isScrollBtnVisible.value =
			$messagesArea.value.scrollTop + $messagesArea.value.offsetHeight < ($messagesArea.value.scrollHeight - 50)

		updateChatMessages()

		if (chat.value) {
			chatStore.saveChatScrollPosition(chat.value.Id, $messagesArea.value.scrollTop)
		}
	}
	$messagesArea.value?.addEventListener('scroll', scrollHandler)
})


onBeforeUnmount(() => {
	loadPrevThrottled.cancel()
	loadNextThrottled.cancel()
	if (scrollHandler && $messagesArea.value) {
		$messagesArea.value.removeEventListener('scroll', scrollHandler)
		scrollHandler = null
	}
});

defineExpose({ focusSync, focusAsync })
</script>

<style scoped lang="scss">
.chat-messages-panel {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	width: 100%;
	background-color: #fff;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
	overflow: auto;

	&::v-deep(.q-message-name) {
		color: #4D4D4D;
		font-weight: 500;
	}

	&::v-deep(.q-message-text-content) {
		color: #4D4D4D;
	}

	&::v-deep(.q-message-text) {
		padding: 8px !important;
	}

	&::v-deep(.q-message) {
		padding: 8px 6px;
	}

	&__pagination-loader {
		display: flex;
		align-items: center;
		justify-content: center;
		position: absolute;
		left: 0;
		right: 0;
		height: 60px;
		pointer-events: none;
		z-index: 2;

		&--top {
			top: 0;
		}

		&--bottom {
			bottom: 52px;
		}
	}

	&__scroll-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		position: absolute;
		height: 30px;
		min-height: 30px;
		width: 30px;
		min-width: 30px;
		border-radius: 100%;
		top: -50px;
		right: 30px;
		background-color: #979797;
		cursor: pointer;
		transition: background-color 0.1s ease-in-out;

		&:hover {
			background-color: #858585;
		}
	}

	&__messages {
		display: flex;
		flex-direction: column;
		position: relative;
		gap: 8px;
		height: 100%;
		overflow: auto;
		padding: 8px 2px 10px 10px;

		&-reply {
			display: flex;
			align-items: center;
			position: sticky;
			left: -10px;
			top: -10px;
			right: -10px;
			background-color: #fff;
			padding: 4px 8px 4px 0;
			z-index: 1;

			&-spacer {
				flex: 1 1 0;
				min-width: 2px;
				max-width: 24px;
			}

			&-count {
				display: flex;
				align-items: center;
				font-size: 14px;
				line-height: 18px;
				font-weight: 500;
				color: #4D4D4D;
			}

			&-btn {
				display: flex;
				align-items: center;
				gap: 2px;
				color: #979797;
				padding: 7px 16px;
				border-radius: 8px;
				cursor: pointer;

				svg {
					color: #979797 !important;
				}

				&:hover {
					background-color: #F2F2F2;
				}

				&-icon {
					margin-top: -2px;
				}

				&-text {
					font-size: 14px;
					line-height: 18px;
					font-weight: 600;
				}
			}
		}

		&-loading {
			display: none;
		}
	}

	&__input {
		display: flex;
		flex-direction: column;
		align-items: center;
		position: relative;
		width: 100%;
		padding: 10px;

		&-field {
			display: flex;
			align-items: center;
			gap: 4px;
			width: 100%;
		}

		&-answered-message {
			display: flex;
			width: 100%;
			align-items: center;
			margin-bottom: 4px;
			position: relative;
			z-index: 3;
			background-color: #fff;

			.answered-message {
				&__lable {
					width: 2px;
					height: 100%;
					border-top-left-radius: 4px;
					border-bottom-left-radius: 4px;
					background-color: #23844c;
					margin-right: 4px;
				}

				&__body {
					display: flex;
					flex-direction: column;
					width: 100%;
					color: #4D4D4D;

					&-header {
						display: flex;
						align-items: center;
						position: relative;
						gap: 4px;
						margin-bottom: 2px;

						&-name {
							font-size: 14px;
							line-height: 18px;
							font-weight: 500;
							color: #4D4D4D;
						}

						&-date {
							font-size: 12px;
							line-height: 16px;
							color: #979797;
						}

						&-close {
							position: absolute;
							right: 4px;
							color: #979797;
							cursor: pointer;
							font-size: 22px;

							&:hover {
								color: #499259;
							}
						}
					}

					&-text {
						display: -webkit-box;
						-webkit-box-orient: vertical;
						-webkit-line-clamp: 3;
						overflow: hidden;
						text-overflow: ellipsis;
						white-space: pre-wrap;
						font-size: 14px;
						line-height: 18px;
					}
				}
			}
		}
	}
}
</style>
